import { useState, useContext, useEffect } from 'react';
import { Context } from '../../context/context';
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';

import {
    Calendar,
    Views
} from 'react-big-calendar';

import {
    InfoOutlined
} from '@mui/icons-material'


import { Trans, useTranslation } from 'react-i18next';
import api from '../../api';
import TimeKeepingCalendar from '../timekeeping/TimeKeeperCalendar/TimeKeepingCalendar';
import CalendarColors from '../timekeeping/TimeKeeperCalendar/CalendarColors';
import DateFormat from '../../utils/DateFormat';
import DayTypeDialog from './DayTypeDialog';
import { setUser } from '../../context/actions';

const toolTipHolidayMsg =
    <Trans i18nKey="toolTipHolidayMsg" ns="instituteConfigurationPage">
        Diese Einstellung wird benötigt um die täglichen bzw. wöchentlichen Soll-Arbeitsstunden
        pro Mitarbeiter zu ermitteln. Heiligabend und Silvester werden ebenfalls als Feiertage eingetragen,
        obwohl sie keine gesetzlichen Feiertage sind. Sofern Mitarbeitende die Möglichkeit haben, Zeitguthaben
        für Zeitausgleich an Brückentagen aufzubauen, werden die Brückentage für unterstützende Hinweise benötigt.
        Falls es Mitarbeitende am Institut gibt, die auch am Samstag oder Sonntag arbeiten, müssen Brückentage auch
        an diesen Tagen eingetragen werden.
    </Trans>

function EditHolidayPage() {
    const { t } = useTranslation();
    const [{ user }, dispatch] = useContext(Context);
    const [defaultDate, setDefaultDate] = useState(new Date());
    const [institute, setInstitute] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedLocationName, setSelectedLocationName] = useState('');
    const [isHoliday, setIsHoliday] = useState(false);
    const [isClosingDay, setIsClosingDay] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (user.id !== '') {
            api.readInstitute(user.institute).then(res => {
                setInstitute(res);
                if (res.locations.length > 0) {
                    setSelectedLocation(res.locations[0]);
                    setSelectedLocationName(res.locations[0].name);
                };
            });
        };
    }, [user]);

    const handleChangeLocation = (locationName) => {
        setSelectedLocationName(locationName);
        let location = institute.locations.find(l => l.name === locationName);
        setSelectedLocation(location);
    };

    const handleUpdateDayType = (newType) => {
        let date = DateFormat.formatISOStringWithTimeZone(selectedDate).split('T')[0];

        let newClosingDays = selectedLocation.closingDays.filter(d => {
            let cd = DateFormat.formatISOStringWithTimeZone(d).split('T')[0];
            if (cd === date) {
                return false;
            };
            return true;
        });

        let newHolidays = selectedLocation.holidays.filter(d => {
            let hd = DateFormat.formatISOStringWithTimeZone(d).split('T')[0];
            if (hd === date) {
                return false;
            };
            return true;
        });

        switch (newType) {
            case 'holiday':
                newHolidays = [...newHolidays, new Date(date)];
                break;
            case 'closingDay':
                newClosingDays = [...newClosingDays, new Date(date)];
                break;
            default:
                break;
        }
        let newLocation = {
            ...selectedLocation,
            holidays: newHolidays,
            closingDays: newClosingDays,
        };

        setSelectedLocation(newLocation);

        api.updateLocation(selectedLocation.id, newLocation).then(res => {
            api.readInstitute(user.institute).then(res => {
                setInstitute(res);
            });
        });

        api.readUser(user.id).then(res=> {
            dispatch(setUser(res))
        })
    };

    const onSelectSlot = ({ start }) => {
        let date = DateFormat.formatISOStringWithTimeZone(start).split('T')[0];

        let isHoliday = selectedLocation.holidays.find(d => {
            let hd = DateFormat.formatISOStringWithTimeZone(d).split('T')[0];
            if (hd === date) {
                return true;
            };
            return false;
        });

        let isClosingDay = selectedLocation.closingDays.find(d => {
            let cd = DateFormat.formatISOStringWithTimeZone(d).split('T')[0];
            if (cd === date) {
                return true;
            };
            return false;
        });

        setIsHoliday(Boolean(isHoliday));
        setIsClosingDay(Boolean(isClosingDay));
        setSelectedDate(start);
        setOpen(true);
    };

    return (
        <>
            <Card sx={{ width: '100vw', p: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Box sx={{ flex: 3 }}>
                            <Box sx={{ display: 'flex', flexDirection: "row", alignItems: 'center', marginBottom: 3 }}>
                                <Typography variant="h5"><Trans i18nKey="feiertage" ns="instituteConfigurationPage">Feiertage</Trans></Typography>
                                <Tooltip title={toolTipHolidayMsg} placement="right-end">
                                    <InfoOutlined sx={{ pl: 1 }} />
                                </Tooltip>
                            </Box>
                            <Autocomplete
                                freeSolo
                                disableClearable
                                value={selectedLocationName}
                                onChange={(event, newValue) => handleChangeLocation(newValue)}
                                options={institute?.locations.map((location) => location.name) || ['']}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('standort', { ns: 'instituteConfigurationPage', defaultValue: 'Standort' })}
                                        InputProps={{
                                            ...params.InputProps,
                                            type: 'search',
                                        }}
                                        variant="standard"
                                    />
                                )}
                            />
                        </Box>
                        <Box sx={{ flex: 12, height: 'fit-content' }}>
                            <TimeKeepingCalendar
                                workDays={[]}
                                workEntries={[]}
                                date={defaultDate}
                                onNavigate={(newDate) => setDefaultDate(newDate)}
                                onSelectSlot={onSelectSlot}
                                location={selectedLocation}
                                legend={[
                                    { label: t('feiertag', { ns: 'timeKeepingPage', defaultValue: 'Feiertag' }), color: CalendarColors.get('holiday') },
                                    { label: t('brückentag', { ns: 'timeKeepingPage', defaultValue: 'Brückentag' }), color: CalendarColors.get('closingDay') },
                                    { label: t('heute', { ns: 'timeKeepingPage', defaultValue: 'Heute' }), color: CalendarColors.get('today') },
                                ]}
                                view={Views.MONTH}
                                views={[Views.MONTH]}
                            />
                            <DayTypeDialog
                                open={open}
                                onClose={() => setOpen(false)}
                                isHoliday={isHoliday}
                                isClosingDay={isClosingDay}
                                date={selectedDate}
                                onSave={handleUpdateDayType}
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </>
    );
};

export default EditHolidayPage
